import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import AppLayout from '../../components/AppLayout';
import SchemeDetailsDialog from '../../components/SchemeDetailsDialog';
import RecordSchemePaymentDialog from '../../components/RecordSchemePaymentDialog';
import CreateSchemeDialog from '../../components/CreateSchemeDialog';
import EditSchemeDialog from '../../components/EditSchemeDialog';
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";
import API_BASE_URL from "../../config/api";

const API_URL = `${API_BASE_URL}/api`;

const CustomerSchemePage = () => {
  const { customerTokenNo } = useParams();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Menu states for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSchemeForMenu, setSelectedSchemeForMenu] = useState(null);

  useEffect(() => {
    if (customerTokenNo) {
      fetchCustomerInfo();
      fetchSchemes();
    }
  }, [customerTokenNo]);

  const fetchCustomerInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/customers/${customerTokenNo}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Handle different response structures
      const customerData = response.data.data || response.data;
      setCustomer({
        ...customerData,
        TokenNo: customerData.TokenNo || customerTokenNo,
        CustomerName: customerData.CustomerName || customerData.Name || 'Unknown Customer',
        PhoneNumber: customerData.PhoneNumber || customerData.Phone || '-'
      });
    } catch (err) {
      console.error('Error fetching customer:', err);
      // Set basic customer info even if fetch fails
      setCustomer({ 
        TokenNo: customerTokenNo,
        CustomerName: 'Loading...',
        PhoneNumber: '-'
      });
    }
  };

  const fetchSchemes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/schemes/customer/${customerTokenNo}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Handle different response structures
      const schemesData = response.data.schemes || response.data.data?.schemes || response.data || [];
      setSchemes(Array.isArray(schemesData) ? schemesData : []);
    } catch (err) {
      console.error('Error fetching schemes:', err);
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to fetch schemes');
      setSchemes([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if scheme is expired
  const isSchemeExpired = (scheme) => {
    return new Date(scheme.ExpiryDate) < new Date();
  };

  // Get dynamic status label based on payment count
  const getDisplayStatus = (scheme) => {
    // If expired, show Expired
    if (isSchemeExpired(scheme)) {
      return 'Expired';
    }
    // If payment count is less than 13, always show Active-Contribution
    if (scheme.PaymentCount < 13) {
      return 'Active-Contribution';
    }
    // Otherwise show the actual status
    return scheme.Status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Contribution': 'info',
      'Ready': 'success',
      'In-Use': 'warning',
      'Expired': 'default',
      'Failed': 'error',
      // Legacy statuses for backward compatibility
      'Active-Contribution': 'info',
      'Ready-For-Bonus': 'success',
      'Active-Usage': 'warning',
      'Incomplete': 'error'
    };
    return colors[status] || 'default';
  };

  const handleViewDetails = (scheme) => {
    setSelectedScheme(scheme);
    setDetailsDialogOpen(true);
  };

  const handleRecordPayment = (scheme) => {
    setSelectedScheme(scheme);
    setPaymentDialogOpen(true);
  };

  const handleEditScheme = (scheme) => {
    setSelectedScheme(scheme);
    setEditDialogOpen(true);
  };

  const handleDeleteScheme = async (schemeId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/schemes/${schemeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scheme deleted successfully');
      setDeleteConfirm(null);
      fetchSchemes();
    } catch (err) {
      console.error('Error deleting scheme:', err);
      toast.error(err.response?.data?.error?.message || 'Failed to delete scheme');
    }
  };

  const handleMenuClick = (event, scheme) => {
    setAnchorEl(event.currentTarget);
    setSelectedSchemeForMenu(scheme);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSchemeForMenu(null);
  };

  const handleMenuAction = (action) => {
    if (!selectedSchemeForMenu) return;
    
    switch (action) {
      case 'view':
        handleViewDetails(selectedSchemeForMenu);
        break;
      case 'payment':
        handleRecordPayment(selectedSchemeForMenu);
        break;
      case 'edit':
        handleEditScheme(selectedSchemeForMenu);
        break;
      case 'delete':
        setDeleteConfirm(selectedSchemeForMenu.SchemeId);
        break;
    }
    handleMenuClose();
  };

  return (
    <AppLayout>
      <Box sx={{ 
        maxWidth: "1400px", 
        mx: "auto", 
        p: { xs: 1, sm: 2, md: 3 },
        bgcolor: "#0a0a0a",
        minHeight: "100vh"
      }}>
        {/* HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            letterSpacing: { xs: 1, sm: 2 },
            color: "#C59A58",
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.5rem" },
            wordBreak: "break-word",
            flex: 1,
            minWidth: 0
          }}>
            CUSTOMER SCHEMES
          </Typography>
          <Chip 
            label={`Token #${customerTokenNo}`} 
            sx={{ 
              bgcolor: "#C59A58", 
              color: "#000", 
              fontWeight: 600,
              fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
              px: { xs: 1, sm: 1.5, md: 2 },
              py: { xs: 1.5, sm: 2, md: 2.5 },
              height: { xs: 28, sm: 36, md: 40 },
              minWidth: "fit-content"
            }} 
          />
        </Box>

        {/* BACK BUTTON */}
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/customers')}
          sx={{ 
            mb: 2,
            borderColor: "#C59A58",
            color: "#C59A58",
            "&:hover": { borderColor: "#b68649", bgcolor: "rgba(197, 154, 88, 0.04)" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 0.75, sm: 1 }
          }}
        >
          Back to Customers
        </Button>

        {/* CUSTOMER INFO CARD */}
        <Paper elevation={6} sx={{ 
          p: { xs: 1.5, sm: 2, md: 3 }, 
          mb: 3, 
          borderRadius: 2, 
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          width: "100%"
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: "#C59A58",
              fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" }
            }}>
              Customer Information
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "center" }}>
              <Avatar
                sx={{
                  width: { xs: 80, sm: 90, md: 100 },
                  height: { xs: 80, sm: 90, md: 100 },
                  bgcolor: customer ? getAvatarColor(customer.CustomerName || customer.TokenNo) : "#666",
                  fontSize: { xs: "1.8rem", sm: "2rem", md: "2.5rem" },
                  fontWeight: 600,
                }}
              >
                {customer ? getInitials(customer.CustomerName || customer.TokenNo) : customerTokenNo.slice(0, 2)}
              </Avatar>
            </Grid>

            <Grid item xs={12} md={9}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Customer Name
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                mb: 2
              }}>
                {customer?.CustomerName || 'Loading...'}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Phone Number
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                {customer?.PhoneNumber || '-'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* ACTION BUTTONS */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700,
            color: "#C59A58",
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" }
          }}>
            Scheme Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              fontWeight: 600,
              color: "#000",
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 0.6, sm: 0.8 },
              flex: { xs: 1, sm: "none" }
            }}
          >
            Create New Scheme
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            py: 4,
            bgcolor: "#1a1a1a",
            borderRadius: 2,
            border: "1px solid #333"
          }}>
            <CircularProgress sx={{ color: "#C59A58" }} />
          </Box>
        ) : error ? (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              bgcolor: "#2a1a1a",
              color: "#fff",
              border: "1px solid #d32f2f",
              '& .MuiAlert-icon': { color: "#d32f2f" }
            }}
          >
            {error}
          </Alert>
        ) : schemes.length === 0 ? (
          <Paper sx={{ 
            p: 6, 
            textAlign: 'center',
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2
          }}>
            <Typography variant="h6" sx={{ color: "#C59A58", mb: 2 }}>
              No schemes found for this customer
            </Typography>
            <Typography variant="body2" sx={{ color: "#888", mb: 3 }}>
              Create a new scheme to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ 
                bgcolor: "#C59A58", 
                "&:hover": { bgcolor: "#b68649" },
                color: "#000",
                fontWeight: 600
              }}
            >
              Create First Scheme
            </Button>
          </Paper>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={6} md={3}>
                <Paper elevation={6} sx={{ 
                  p: { xs: 1, sm: 1.5, md: 2 }, 
                  borderRadius: 2, 
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                  border: "1px solid #333",
                  minHeight: { xs: 80, sm: 100, md: 120 }
                }}>
                  <Typography variant="caption" sx={{ 
                    color: "#C59A58", 
                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                    display: "block",
                    mb: 0.5
                  }}>
                    Total Schemes
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 600, 
                    color: "#fff",
                    fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2rem" },
                    lineHeight: 1
                  }}>
                    {schemes.length}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={6} md={3}>
                <Paper elevation={6} sx={{ 
                  p: { xs: 1, sm: 1.5, md: 2 }, 
                  borderRadius: 2, 
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                  border: "1px solid #333",
                  minHeight: { xs: 80, sm: 100, md: 120 }
                }}>
                  <Typography variant="caption" sx={{ 
                    color: "#C59A58", 
                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                    display: "block",
                    mb: 0.5
                  }}>
                    Total Credit
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 600, 
                    color: "#4caf50",
                    fontSize: { xs: "1rem", sm: "1.2rem", md: "1.8rem" },
                    lineHeight: 1
                  }}>
                    ₹{schemes.reduce((sum, s) => {
                      const credit = (s.PaymentCount >= 13 && !isSchemeExpired(s)) ? parseFloat(s.TotalCredit || 0) : 0;
                      return sum + credit;
                    }, 0).toFixed(0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={6} md={3}>
                <Paper elevation={6} sx={{ 
                  p: { xs: 1, sm: 1.5, md: 2 }, 
                  borderRadius: 2, 
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                  border: "1px solid #C59A58",
                  minHeight: { xs: 80, sm: 100, md: 120 }
                }}>
                  <Typography variant="caption" sx={{ 
                    color: "#C59A58", 
                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                    display: "block",
                    mb: 0.5
                  }}>
                    Available Credit
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 600, 
                    color: "#9c27b0",
                    fontSize: { xs: "1rem", sm: "1.2rem", md: "1.8rem" },
                    lineHeight: 1
                  }}>
                    ₹{schemes.reduce((sum, s) => {
                      const credit = (s.PaymentCount >= 13 && !isSchemeExpired(s)) ? parseFloat(s.AvailableCredit || 0) : 0;
                      return sum + credit;
                    }, 0).toFixed(0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={6} md={3}>
                <Paper elevation={6} sx={{ 
                  p: { xs: 1, sm: 1.5, md: 2 }, 
                  borderRadius: 2, 
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                  border: "1px solid #333",
                  minHeight: { xs: 80, sm: 100, md: 120 }
                }}>
                  <Typography variant="caption" sx={{ 
                    color: "#C59A58", 
                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                    display: "block",
                    mb: 0.5
                  }}>
                    Active Schemes
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 600, 
                    color: "#2196f3",
                    fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2rem" },
                    lineHeight: 1
                  }}>
                    {schemes.filter(s => !isSchemeExpired(s) && s.PaymentCount < 13).length}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Schemes Table */}
            <Paper elevation={6} sx={{ 
              borderRadius: 2, 
              bgcolor: "#1a1a1a",
              border: "1px solid #333",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              p: { xs: 0.5, sm: 1, md: 2 }
            }}>
              <Box sx={{ overflowX: "auto", width: "100%" }}>
                <TableContainer sx={{ 
                  borderRadius: 2,
                  minWidth: { xs: 320, sm: 600, md: 800 }
                }}>
                  <Table
                    sx={{
                      borderCollapse: "separate",
                      borderSpacing: "0 4px",
                    }}
                    aria-label="schemes table"
                  >
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#C59A58" }}>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap"
                        }}>
                          ID
                        </TableCell>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap"
                        }}>
                          Installment
                        </TableCell>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap"
                        }}>
                          Payments
                        </TableCell>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap",
                          display: { xs: "none", sm: "table-cell" }
                        }}>
                          Total Paid
                        </TableCell>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap"
                        }}>
                          Credit
                        </TableCell>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap"
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap",
                          display: { xs: "none", md: "table-cell" }
                        }}>
                          Start Date
                        </TableCell>
                        <TableCell sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap",
                          display: { xs: "none", lg: "table-cell" }
                        }}>
                          Expiry Date
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          color: "#000", 
                          fontWeight: 700, 
                          border: "none",
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.875rem" },
                          p: { xs: 0.5, sm: 0.75, md: 1 },
                          whiteSpace: "nowrap",
                          width: { xs: "40px", sm: "50px", md: "60px" }
                        }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {schemes.map((scheme, index) => (
                        <TableRow
                          key={scheme.SchemeId}
                          sx={{
                            bgcolor: index % 2 === 0 ? "#2a2a2a" : "#333",
                            borderRadius: 1,
                            "&:hover": { boxShadow: 1 },
                            "& td": { 
                              border: "none", 
                              verticalAlign: "middle",
                              fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.75rem" },
                              p: { xs: 0.4, sm: 0.6, md: 1 }
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: "#C59A58", whiteSpace: "nowrap" }}>
                            {scheme.SchemeId}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#4caf50", whiteSpace: "nowrap" }}>
                            ₹{parseFloat(scheme.MonthlyInstallment || 0).toFixed(0)}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip 
                              label={`${scheme.PaymentCount || 0}/13`} 
                              size="small"
                              sx={{
                                bgcolor: (scheme.PaymentCount || 0) >= 13 ? "#2e7d32" : "#1976d2",
                                color: "white",
                                fontWeight: 600,
                                fontSize: { xs: "0.5rem", sm: "0.6rem" },
                                height: { xs: 20, sm: 24 }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: "#4caf50", 
                            whiteSpace: "nowrap",
                            display: { xs: "none", sm: "table-cell" }
                          }}>
                            ₹{parseFloat(scheme.TotalPaid || 0).toFixed(0)}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600,
                            color: ((scheme.PaymentCount || 0) >= 13 && !isSchemeExpired(scheme) && parseFloat(scheme.AvailableCredit || 0) > 0) ? "#9c27b0" : "#888",
                            whiteSpace: "nowrap"
                          }}>
                            ₹{((scheme.PaymentCount || 0) >= 13 && !isSchemeExpired(scheme)) ? parseFloat(scheme.AvailableCredit || 0).toFixed(0) : '0'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip
                              label={getDisplayStatus(scheme)}
                              size="small"
                              sx={{
                                bgcolor: getDisplayStatus(scheme) === 'Active-Contribution' ? "#1976d2" :
                                         getDisplayStatus(scheme) === 'Ready' ? "#2e7d32" :
                                         getDisplayStatus(scheme) === 'In-Use' ? "#ed6c02" :
                                         getDisplayStatus(scheme) === 'Expired' ? "#666" : "#d32f2f",
                                color: "white",
                                fontWeight: 600,
                                fontSize: { xs: "0.5rem", sm: "0.6rem" },
                                height: { xs: 20, sm: 24 }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            color: "#fff", 
                            whiteSpace: "nowrap",
                            display: { xs: "none", md: "table-cell" }
                          }}>
                            {scheme.StartDate ? new Date(scheme.StartDate).toLocaleDateString('en-GB') : '-'}
                          </TableCell>
                          <TableCell sx={{ 
                            color: "#fff", 
                            whiteSpace: "nowrap",
                            display: { xs: "none", lg: "table-cell" }
                          }}>
                            {scheme.ExpiryDate ? new Date(scheme.ExpiryDate).toLocaleDateString('en-GB') : '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ width: { xs: "40px", sm: "50px", md: "60px" } }}>
                            <IconButton
                              onClick={(e) => handleMenuClick(e, scheme)}
                              sx={{ 
                                color: "#C59A58",
                                p: { xs: 0.25, sm: 0.5 }
                              }}
                              size="small"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </>
        )}
      </Box>

      {/* Scheme Details Dialog */}
      <SchemeDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedScheme(null);
        }}
        schemeId={selectedScheme?.SchemeId}
        onRecordPayment={handleRecordPayment}
        onDataChange={fetchSchemes}
      />

      {/* Record Payment Dialog */}
      <RecordSchemePaymentDialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setSelectedScheme(null);
        }}
        scheme={selectedScheme}
        onSuccess={fetchSchemes}
      />

      {/* Create Scheme Dialog */}
      <CreateSchemeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        customer={customer || { TokenNo: customerTokenNo }}
        onSuccess={fetchSchemes}
      />

      {/* Edit Scheme Dialog */}
      <EditSchemeDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedScheme(null);
        }}
        scheme={selectedScheme}
        onSuccess={fetchSchemes}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={!!deleteConfirm} 
        onClose={() => setDeleteConfirm(null)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#fff", mb: 2 }}>
            Are you sure you want to delete this scheme? This will permanently delete:
          </Typography>
          <Box component="ul" sx={{ mt: 1, color: "#fff" }}>
            <li>All payment records</li>
            <li>Bonus transactions</li>
            <li>Credit usage history</li>
          </Box>
          <Typography sx={{ mt: 2, fontWeight: 600, color: "#d32f2f" }}>
            This action cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleDeleteScheme(deleteConfirm)} 
            color="error" 
            variant="contained"
          >
            Delete Scheme
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            border: "1px solid #444",
            borderRadius: 1,
            minWidth: 160
          }
        }}
      >
        <MenuItem 
          onClick={() => handleMenuAction('view')}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <ViewIcon sx={{ mr: 1, fontSize: "1rem", color: "#1976d2" }} />
          View Details
        </MenuItem>
        <MenuItem 
          onClick={() => handleMenuAction('payment')}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <PaymentIcon sx={{ mr: 1, fontSize: "1rem", color: "#2e7d32" }} />
          Manage Payments
        </MenuItem>
        <MenuItem 
          onClick={() => handleMenuAction('edit')}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: "1rem", color: "#C59A58" }} />
          Edit Scheme
        </MenuItem>
        <MenuItem 
          onClick={() => handleMenuAction('delete')}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: "1rem", color: "#f44336" }} />
          Delete Scheme
        </MenuItem>
      </Menu>
    </AppLayout>
  );
};

export default CustomerSchemePage;
